// externals
import React, { useCallback } from 'react';

// components and formatters
import { OpenRollToPaymentSlideOutButton, PaymentLoading, RollToOptionsInterstate } from '@makemydeal/dr-dash-components';
import { DealTypeChangeNotice } from './DealTypeChangeNotice';
import { DealTypeLabels } from './DealTypeLabels';
import DealTypeSourceBadge from './DealTypeSourceBadge';
import { useDealTypeDropdown } from './useDealTypeDropdown';

// constants
import { DEAL_TYPE } from '../../constants';

// interstate components
import { Box } from '@interstate/components/Box';
import { CheckmarkSolidIcon, ChevronDownIcon } from '@interstate/components/Icons';
import { Menu } from '@interstate/components/Menu';

// css styling
import './DealTypeDropdown.scss';
import {
    DealTypeDropdownContainer,
    DealTypeLabel,
    CompactDealTypeLabelAndRollToButton,
    CompactDealTypeSelectTrigger
} from './DealTypeDropdown.style';

const triggerContentStyle = {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    minWidth: 0,
    overflow: 'hidden'
};

export const DealTypeDropdown = () => {
    const handleSelectedItemClass = useCallback((offerType: string, dealTypes: string[]) => {
        setTimeout(() => {
            dealTypes.forEach((item, index) => {
                const itemEl = document.querySelector(`#ids-menu-item-deal-type-selector-${index}`) as HTMLElement;
                if (!itemEl) return;

                const isSelected = item.toLowerCase() === offerType.toLowerCase();

                if (isSelected) {
                    itemEl.classList.add('selected-item');
                } else {
                    itemEl.classList.remove('selected-item');
                }
            });
        }, 10);
    }, []);

    const {
        dealTypes,
        selectedDealType,
        menuOptions,
        menuReferenceProps,
        menuWidth,
        openMenu,
        labelLines: [line1, line2],
        isLocked,
        isFlexBuyPayment,
        isExplicitLenderP1Enabled,
        isVPPTemplateProductsLoading,
        isPaymentLoading,
        isDealTypeChangeNoticeShown,
        dismissDealTypeChangeNotice
    } = useDealTypeDropdown({ onDealTypeSelected: handleSelectedItemClass });

    const handleOpenDealTypeDropdown = (e: React.MouseEvent<HTMLElement>) => {
        openMenu(e);
        handleSelectedItemClass(selectedDealType, dealTypes);
    };

    return (
        <DealTypeDropdownContainer>
            <CompactDealTypeLabelAndRollToButton>
                <DealTypeLabel>{DEAL_TYPE}</DealTypeLabel>
                {
                    <>
                        {/*US1652724 [FFB] Remove Roll-To for Flex Buy Deals*/}
                        {!isFlexBuyPayment && <OpenRollToPaymentSlideOutButton />}
                        {!isFlexBuyPayment && <RollToOptionsInterstate />}
                    </>
                }
            </CompactDealTypeLabelAndRollToButton>
            <Box position="relative">
                <PaymentLoading testId="vpp-deal-type-dropdown-loading-indicator" isLoading={isVPPTemplateProductsLoading} />
                <PaymentLoading
                    testId="payment-deal-type-dropdown-loading-indicator"
                    size={20}
                    opacity={0.8}
                    isLoading={isPaymentLoading}
                />
                <CompactDealTypeSelectTrigger
                    sx={{ backgroundColor: ({ tokens }) => tokens.BaseColorWhite }}
                    endIcon={<ChevronDownIcon />}
                    buttonStyle="secondary"
                    data-testid="deal-type-dropdown"
                    onClick={handleOpenDealTypeDropdown}
                    disabled={isLocked}
                >
                    {/* Wrapped only under the toggle: unconditionally, the labels would gain an
                        `overflow: hidden` parent on the toggle-off path and a wrapping label could clip. */}
                    {isExplicitLenderP1Enabled ? (
                        <Box sx={triggerContentStyle}>
                            <DealTypeLabels lineOne={line1} lineTwo={line2} />
                            <DealTypeSourceBadge />
                        </Box>
                    ) : (
                        <DealTypeLabels lineOne={line1} lineTwo={line2} />
                    )}
                </CompactDealTypeSelectTrigger>
            </Box>
            <Menu
                id={'deal-type-selector'}
                width={menuWidth}
                icon={<CheckmarkSolidIcon />}
                referenceProps={menuReferenceProps}
                position={'bottom-start'}
                menuOptions={menuOptions}
            />
            {isDealTypeChangeNoticeShown && <DealTypeChangeNotice onClose={dismissDealTypeChangeNotice} />}
        </DealTypeDropdownContainer>
    );
};
